// src/components/Feedback.js
import React from 'react';
import axios from 'axios';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

export default class Feedback extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: '',
      feedback: [],

      // create form
      title: '',
      note: '',
      type: 'general',

      // user
      user: null,

      // comments
      activeCommentsId: null,
      commentText: '',

      // editing feedback
      editingFeedbackId: null,
      editingFeedbackTitle: '',
      editingFeedbackText: '',

      // editing comment
      editingCommentId: null,
      editingCommentText: '',
      editingCommentFeedbackId: null,
    };
  }

  componentDidMount() {
    this.fetchUserDetail();
  }

  fetchUserDetail = async () => {
    try {
      const res = await axios.get(`${API_BASE}/user/user_detail`, {
        withCredentials: true,
      });

      const raw = res.data || {};
      const user = {
        ...raw,
        userId: raw.userId || raw._id || raw.id,
      };

      this.setState({ user }, () => {
        this.fetchFeedback();
      });
    } catch (err) {
      console.error('Error fetching user detail:', err);
      this.setState({
        error: err?.response?.data?.message || 'Failed to fetch user detail',
      });
    }
  };

  fetchFeedback = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const res = await axios.get(`${API_BASE}/feedback`, {
        withCredentials: true,
      });
      this.setState({ feedback: res.data || [], loading: false });
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to load feedback',
        loading: false,
      });
    }
  };

  handleCreate = async (e) => {
    e.preventDefault();
    const { title, note, type, user } = this.state;
    if (!user) return;
    if (!note.trim()) return;

    try {
      const res = await axios.post(
        `${API_BASE}/feedback`,
        {
          title,
          note,
          type,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        { withCredentials: true }
      );

      this.setState((prev) => ({
        feedback: [res.data, ...prev.feedback],
        title: '',
        note: '',
        type: 'general',
      }));
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to create feedback',
      });
    }
  };

  hasCurrentUserVoted = (fb) => {
    const { user } = this.state;
    if (!user) return false;
    return fb.vote?.some((v) => String(v.userId) === String(user.userId));
  };

  handleVote = async (fb) => {
    const { user } = this.state;
    if (!user) return;

    const currentUserId = user.userId;
    const id = fb._id;

    if (String(fb.userId) === String(currentUserId)) {
      this.setState({ error: "You can't vote on your own feedback." });
      return;
    }

    const alreadyVoted = this.hasCurrentUserVoted(fb);

    try {
      if (!alreadyVoted) {
        const res = await axios.post(
          `${API_BASE}/feedback/${id}/vote`,
          {
            userId: currentUserId,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          { withCredentials: true }
        );
        this.updateFeedbackInState(res.data);
      } else {
        const res = await axios.delete(`${API_BASE}/feedback/${id}/vote`, {
          withCredentials: true,
        });
        this.updateFeedbackInState(res.data);
      }
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to update vote',
      });
    }
  };

  startEditFeedback = (fb) => {
    this.setState({
      editingFeedbackId: fb._id,
      editingFeedbackTitle: fb.title || '',
      editingFeedbackText: fb.note || '',
    });
  };

  cancelEditFeedback = () => {
    this.setState({
      editingFeedbackId: null,
      editingFeedbackTitle: '',
      editingFeedbackText: '',
    });
  };

  saveEditFeedback = async () => {
    const {
      editingFeedbackId,
      editingFeedbackTitle,
      editingFeedbackText,
    } = this.state;
    if (!editingFeedbackId) return;

    try {
      const res = await axios.put(
        `${API_BASE}/feedback/${editingFeedbackId}`,
        {
          title: editingFeedbackTitle,
          note: editingFeedbackText,
        },
        { withCredentials: true }
      );
      this.updateFeedbackInState(res.data);
      this.cancelEditFeedback();
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to update feedback',
      });
    }
  };

  handleDeleteFeedback = (fb) => {
    confirmAlert({
      title: 'Delete feedback?',
      message: 'This action cannot be undone.',
      buttons: [
        {
          label: 'Yes, delete',
          onClick: async () => {
            try {
              await axios.delete(`${API_BASE}/feedback/${fb._id}`, {
                withCredentials: true,
              });
              this.setState((prev) => ({
                feedback: prev.feedback.filter((f) => f._id !== fb._id),
              }));
            } catch (err) {
              this.setState({
                error:
                  err?.response?.data?.message || 'Failed to delete feedback',
              });
            }
          },
        },
        { label: 'Cancel' },
      ],
    });
  };

  handleAddComment = async (fbId) => {
    const { commentText, user } = this.state;
    if (!commentText.trim() || !user) return;

    try {
      const res = await axios.post(
        `${API_BASE}/feedback/${fbId}/comments`,
        {
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          comment: commentText,
        },
        { withCredentials: true }
      );
      this.updateFeedbackInState(res.data);
      this.setState({ commentText: '' });
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to add comment',
      });
    }
  };

  startEditComment = (fbId, comment) => {
    this.setState({
      editingCommentId: comment._id,
      editingCommentText: comment.comment,
      editingCommentFeedbackId: fbId,
    });
  };

  cancelEditComment = () => {
    this.setState({
      editingCommentId: null,
      editingCommentText: '',
      editingCommentFeedbackId: null,
    });
  };

  saveEditComment = async () => {
    const {
      editingCommentId,
      editingCommentText,
      editingCommentFeedbackId,
    } = this.state;
    if (!editingCommentId || !editingCommentFeedbackId) return;

    try {
      const res = await axios.put(
        `${API_BASE}/feedback/${editingCommentFeedbackId}/comments/${editingCommentId}`,
        { comment: editingCommentText },
        { withCredentials: true }
      );
      this.updateFeedbackInState(res.data);
      this.cancelEditComment();
    } catch (err) {
      this.setState({
        error: err?.response?.data?.message || 'Failed to update comment',
      });
    }
  };

  handleDeleteComment = (fbId, commentId) => {
    confirmAlert({
      title: 'Delete comment?',
      message: 'This action cannot be undone.',
      buttons: [
        {
          label: 'Yes, delete',
          onClick: async () => {
            try {
              const res = await axios.delete(
                `${API_BASE}/feedback/${fbId}/comments/${commentId}`,
                { withCredentials: true }
              );
              this.updateFeedbackInState(res.data);
            } catch (err) {
              this.setState({
                error:
                  err?.response?.data?.message || 'Failed to delete comment',
              });
            }
          },
        },
        { label: 'Cancel' },
      ],
    });
  };

  updateFeedbackInState = (updated) => {
    this.setState((prev) => ({
      feedback: prev.feedback.map((fb) =>
        fb._id === updated._id ? updated : fb
      ),
    }));
  };

  toggleComments = (id) => {
    this.setState((prev) => ({
      activeCommentsId: prev.activeCommentsId === id ? null : id,
      commentText: '',
    }));
  };

  getTypeClasses = (type) => {
    switch (type) {
      case 'bug':
        return 'bg-rose-100 text-rose-700 border border-rose-200';
      case 'feature':
        return 'bg-violet-100 text-violet-700 border border-violet-200';
      case 'clinical':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  getStatusClasses = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'done':
      case 'completed':
      case 'closed':
        return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      case 'in progress':
      case 'progress':
        return 'bg-amber-100 text-amber-700 border border-amber-200';
      default:
        return 'bg-sky-100 text-sky-700 border border-sky-200';
    }
  };

  render() {
    const {
      loading,
      error,
      feedback,
      title,
      note,
      type,
      activeCommentsId,
      commentText,
      user,
      editingFeedbackId,
      editingFeedbackTitle,
      editingFeedbackText,
      editingCommentId,
      editingCommentText,
    } = this.state;

    const currentUserId = user ? user.userId : null;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-950 px-4 py-8 md:px-8 lg:px-12">
        <div className="mx-auto max-w-6xl">
          {/* hero */}
          <div className="mb-8 overflow-hidden rounded-[28px] border border-white/10 bg-white/8 backdrop-blur-xl shadow-2xl">
            <div className="grid gap-6 p-6 md:grid-cols-[1.2fr_0.8fr] md:p-8">
              <div>
                <div className="mb-3 inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
                  Community board
                </div>
                <h2 className="text-3xl font-bold tracking-tight text-white md:text-5xl">
                  Feedback &amp; Suggestions
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 md:text-base">
                  Share bugs, ideas, clinical workflow suggestions, and product
                  improvements. Vote on what matters most and discuss ideas with
                  the team.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    Total
                  </p>
                  <p className="mt-2 text-2xl font-bold text-white">
                    {feedback.length}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    Open
                  </p>
                  <p className="mt-2 text-2xl font-bold text-white">
                    {
                      feedback.filter(
                        (x) =>
                          !x.status ||
                          ['open', 'new'].includes(
                            String(x.status).toLowerCase()
                          )
                      ).length
                    }
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    Comments
                  </p>
                  <p className="mt-2 text-2xl font-bold text-white">
                    {feedback.reduce(
                      (sum, item) => sum + (item.comments?.length || 0),
                      0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 rounded-2xl border border-red-300/30 bg-red-500/10 px-4 py-3 text-sm text-red-100 backdrop-blur">
              {error}
            </div>
          )}

          {/* create form */}
          <form
            onSubmit={this.handleCreate}
            className="mb-8 overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl"
          >
            <div className="border-b border-slate-200 bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-6 py-5">
              <h3 className="text-xl font-semibold text-white">
                Share new feedback
              </h3>
              <p className="mt-1 text-sm text-sky-100/80">
                Suggest a feature, report a bug, or raise a clinical workflow
                idea.
              </p>
            </div>

            <div className="grid gap-5 p-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Title
                </label>
                <input
                  value={title}
                  onChange={(e) => this.setState({ title: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                  placeholder="e.g. Quick template for MHCP"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Feedback note
                </label>
                <textarea
                  value={note}
                  onChange={(e) => this.setState({ note: e.target.value })}
                  className="h-36 w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                  placeholder="e.g. Can we add a quick template for mental health care plan..."
                />
              </div>

              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-semibold text-slate-700">
                    Type
                  </label>
                  <select
                    value={type}
                    onChange={(e) => this.setState({ type: e.target.value })}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                  >
                    <option value="general">General</option>
                    <option value="bug">Bug</option>
                    <option value="feature">Feature request</option>
                    <option value="clinical">Clinical</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-2xl bg-sky-700 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-sky-800"
                >
                  Submit feedback
                </button>
              </div>
            </div>
          </form>

          {/* list */}
          {loading ? (
            <div className="rounded-3xl border border-white/10 bg-white/10 px-6 py-10 text-center text-slate-200 backdrop-blur">
              Loading feedback…
            </div>
          ) : feedback.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 px-6 py-12 text-center text-slate-300 backdrop-blur">
              No feedback yet.
            </div>
          ) : (
            <div className="space-y-5">
              {feedback.map((fb) => {
                const isOwn =
                  currentUserId &&
                  fb.userId &&
                  String(fb.userId) === String(currentUserId);
                const voted = this.hasCurrentUserVoted(fb);

                return (
                  <div
                    key={fb._id}
                    className="overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl"
                  >
                    <div className="grid gap-0 lg:grid-cols-[110px_1fr]">
                      {/* vote panel */}
                      <div className="flex flex-row items-center justify-between border-b border-slate-100 bg-slate-50 p-4 lg:flex-col lg:justify-start lg:border-b-0 lg:border-r">
                        <button
                          disabled={!currentUserId || isOwn}
                          onClick={() => this.handleVote(fb)}
                          className={`flex min-w-[78px] flex-col items-center justify-center rounded-2xl px-4 py-3 text-xs font-semibold shadow-sm transition
                            ${
                              !currentUserId || isOwn
                                ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                                : voted
                                ? 'bg-amber-400 text-amber-950 hover:bg-amber-500'
                                : 'bg-slate-800 text-white hover:bg-slate-900'
                            }`}
                        >
                          <span className="text-lg font-bold">
                            {fb.vote ? fb.vote.length : 0}
                          </span>
                          <span>{isOwn ? 'My post' : voted ? 'Voted' : 'Vote'}</span>
                        </button>

                        <div className="mt-0 text-right lg:mt-4 lg:text-center">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400">
                            Comments
                          </p>
                          <p className="text-lg font-bold text-slate-800">
                            {fb.comments ? fb.comments.length : 0}
                          </p>
                        </div>
                      </div>

                      {/* content */}
                      <div className="p-5 md:p-6">
                        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                          <div className="flex-1">
                            {editingFeedbackId === fb._id ? (
                              <div className="space-y-3">
                                <input
                                  value={editingFeedbackTitle}
                                  onChange={(e) =>
                                    this.setState({
                                      editingFeedbackTitle: e.target.value,
                                    })
                                  }
                                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                                  placeholder="Title"
                                />
                                <textarea
                                  value={editingFeedbackText}
                                  onChange={(e) =>
                                    this.setState({
                                      editingFeedbackText: e.target.value,
                                    })
                                  }
                                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                                  rows={4}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={this.saveEditFeedback}
                                    className="rounded-xl bg-sky-700 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-800"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={this.cancelEditFeedback}
                                    className="rounded-xl bg-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-300"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                  <span
                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${this.getTypeClasses(
                                      fb.type || 'general'
                                    )}`}
                                  >
                                    {fb.type || 'general'}
                                  </span>
                                  <span
                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${this.getStatusClasses(
                                      fb.status || 'open'
                                    )}`}
                                  >
                                    {fb.status || 'open'}
                                  </span>
                                </div>

                                <h3 className="text-xl font-bold text-slate-900">
                                  {fb.title || 'Untitled feedback'}
                                </h3>

                                <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-slate-700 md:text-[15px]">
                                  {fb.note}
                                </p>

                                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                  <span className="rounded-full bg-slate-100 px-3 py-1">
                                    {fb.firstName || ''} {fb.lastName || ''}
                                  </span>
                                  {fb.date && (
                                    <span className="rounded-full bg-slate-100 px-3 py-1">
                                      {new Date(fb.date).toLocaleDateString(
                                        'en-AU'
                                      )}
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>

                          {isOwn && editingFeedbackId !== fb._id && (
                            <div className="flex shrink-0 gap-2">
                              <button
                                onClick={() => this.startEditFeedback(fb)}
                                className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => this.handleDeleteFeedback(fb)}
                                className="rounded-xl bg-red-500 px-4 py-2 text-xs font-semibold text-white hover:bg-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>

                        {/* comments */}
                        <div className="mt-6 border-t border-slate-100 pt-4">
                          <button
                            onClick={() => this.toggleComments(fb._id)}
                            className="inline-flex items-center rounded-full bg-sky-50 px-4 py-2 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
                          >
                            {activeCommentsId === fb._id
                              ? 'Hide comments'
                              : `View comments (${fb.comments ? fb.comments.length : 0})`}
                          </button>

                          {activeCommentsId === fb._id && (
                            <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 md:p-5">
                              <div className="space-y-3">
                                {fb.comments && fb.comments.length > 0 ? (
                                  fb.comments.map((c) => {
                                    const canModifyComment =
                                      currentUserId &&
                                      (String(c.userId) ===
                                        String(currentUserId) ||
                                        String(fb.userId) ===
                                          String(currentUserId));
                                    const isEditingThisComment =
                                      editingCommentId === c._id;

                                    return (
                                      <div
                                        key={c._id}
                                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                                      >
                                        <div className="flex gap-3">
                                          <div className="flex-1">
                                            {isEditingThisComment ? (
                                              <>
                                                <input
                                                  value={editingCommentText}
                                                  onChange={(e) =>
                                                    this.setState({
                                                      editingCommentText:
                                                        e.target.value,
                                                    })
                                                  }
                                                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                                                />
                                                <div className="mt-3 flex gap-2">
                                                  <button
                                                    onClick={
                                                      this.saveEditComment
                                                    }
                                                    className="rounded-xl bg-sky-700 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-800"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={
                                                      this.cancelEditComment
                                                    }
                                                    className="rounded-xl bg-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-300"
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                <p className="text-sm leading-6 text-slate-800">
                                                  {c.comment}
                                                </p>
                                                <p className="mt-2 text-[11px] text-slate-500">
                                                  {c.firstName} {c.lastName}
                                                  {c.date
                                                    ? ` • ${new Date(
                                                        c.date
                                                      ).toLocaleString(
                                                        'en-AU'
                                                      )}`
                                                    : ''}
                                                </p>
                                              </>
                                            )}
                                          </div>

                                          {canModifyComment &&
                                            !isEditingThisComment && (
                                              <div className="flex flex-col gap-2">
                                                <button
                                                  onClick={() =>
                                                    this.startEditComment(
                                                      fb._id,
                                                      c
                                                    )
                                                  }
                                                  className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  onClick={() =>
                                                    this.handleDeleteComment(
                                                      fb._id,
                                                      c._id
                                                    )
                                                  }
                                                  className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100"
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-400">
                                    No comments yet.
                                  </div>
                                )}
                              </div>

                              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                <input
                                  type="text"
                                  value={commentText}
                                  onChange={(e) =>
                                    this.setState({
                                      commentText: e.target.value,
                                    })
                                  }
                                  className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                                  placeholder="Write a comment…"
                                />
                                <button
                                  onClick={() => this.handleAddComment(fb._id)}
                                  className="rounded-2xl bg-sky-700 px-5 py-3 text-sm font-semibold text-white hover:bg-sky-800"
                                >
                                  Add comment
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}